import React, { useMemo } from 'react';
import InlineOptions, { type OptionType } from '../inline-options';

import { CurrencyTypeEnum, VolSurfaceToken } from '../../../model/currency_type.enum';
import {
  useAvailableTokens,
  type AvailableToken,
} from '../../../providers/exotics-tokens/use-available-tokens';
import { classifyRegistryTokens, labelRegistryRow } from './registry-rows';
import BitcoinIcon from '../../../assets/icons/currency/BTC.svg';
import EthereumIcon from '../../../assets/icons/currency/ETH.svg';
import SolanaIcon from '../../../assets/icons/currency/Solana.svg';
import RippleIcon from '../../../assets/icons/currency/XRP.svg';
import AvaxIcon from '../../../assets/icons/currency/AVAX.svg';
import TrxIcon from '../../../assets/icons/currency/TRX.svg';
import BinanceCoinIcon from '../../../assets/icons/currency/BinanceCoin.svg';
import CryptoIcon from '../crypto-icon';
import './styles.scss';

interface TokenSelectorProps {
  token: VolSurfaceToken;
  setToken: (value: VolSurfaceToken) => void;
  isFixedToken?: boolean;
  // Opt-in: render the registry-driven two-row (Exchange / Generated) selector
  // instead of the hardcoded single row. The Alt Surface Builder sets this;
  // every other consumer keeps the original behaviour.
  fromRegistry?: boolean;
}

const renderIcon = (icon: string) => {
  if (icon) {
    return <img src={icon} alt="token icon" className="token-icon" />;
  }
  return null;
};

const tokens = [
  { value: CurrencyTypeEnum.BITCOIN, label: 'BTC', icon: renderIcon(BitcoinIcon) },
  { value: CurrencyTypeEnum.ETHEREUM, label: 'ETH', icon: renderIcon(EthereumIcon) },
  { value: CurrencyTypeEnum.SOL, label: 'SOL', icon: renderIcon(SolanaIcon) },
  { value: CurrencyTypeEnum.XRP, label: 'XRP', icon: renderIcon(RippleIcon) },
  { value: CurrencyTypeEnum.AVAX_USDC, label: 'AVAX', icon: renderIcon(AvaxIcon) },
  { value: CurrencyTypeEnum.TRX_USDC, label: 'TRX', icon: renderIcon(TrxIcon) },
  {
    value: CurrencyTypeEnum.HYPE,
    label: 'HYPE',
    // No bundled HYPE.svg; use staging's CDN resolver with graceful GENERIC fallback.
    icon: <CryptoIcon symbol="HYPE" size={32} className="token-icon" />,
  },
];

// Icons for the subset of base currencies that ship an SVG. Anything not
// listed (COMP, DOGE, MORPHO, VIRTUAL, WLFI, ...) renders label-only — see
// renderIcon('') returning null, so a missing icon never crashes.
const REGISTRY_ICONS: Record<string, string> = {
  BTC: BitcoinIcon,
  ETH: EthereumIcon,
  SOL: SolanaIcon,
  XRP: RippleIcon,
  AVAX: AvaxIcon,
  TRX: TrxIcon,
  BNB: BinanceCoinIcon,
};

// `value` is the bare baseQuote (the wire token). The downstream snapshot path
// — SnapshotRequest in get-vol-provider.tsx and subscribe.latest_vol_surface in
// use-live-vol-surface.ts — keys on this token string alone and carries no
// `source`, so we can't source-qualify the value here without a backend
// contract change. If the same baseQuote ever appeared under both an exchange
// and a generated source, both rows' chips would highlight; because the backend
// resolves a surface by baseQuote alone, that stays a cosmetic edge, not a
// wrong-surface bug.
//
// Labels are computed per row (see labelRegistryRow) so chips that would
// otherwise collide stay distinct — label-only, the `value` is untouched.
const toRegistryOptions = (tokens: AvailableToken[]): OptionType[] => {
  const labels = labelRegistryRow(tokens);
  return tokens.map((token, i) => ({
    value: token.baseQuote,
    label: labels[i],
    icon: renderIcon(REGISTRY_ICONS[(token.baseCcy || '').toUpperCase()] ?? ''),
  }));
};

const makeHandleOptionChange =
  (isFixedToken: boolean | undefined, setToken: (value: VolSurfaceToken) => void) =>
  (option: string) => {
    if (isFixedToken) return;

    setToken(option);
  };

const RegistryTokenSelector: React.FC<Omit<TokenSelectorProps, 'fromRegistry'>> = ({
  token,
  setToken,
  isFixedToken,
}) => {
  const { tokens: available, isLoading, error } = useAvailableTokens();

  const { exchangeOptions, generatedOptions } = useMemo(() => {
    const { exchange, generated } = classifyRegistryTokens(available);
    return {
      exchangeOptions: toRegistryOptions(exchange),
      generatedOptions: toRegistryOptions(generated),
    };
  }, [available]);

  const handleOptionChange = makeHandleOptionChange(isFixedToken, setToken);

  // When the token is fixed, mirror the single-row behaviour: show only the
  // selected token (in whichever row it belongs to) and disable interaction.
  const applyFixed = (options: OptionType[]) =>
    isFixedToken ? options.filter((o) => o.value === token) : options;

  const exchangeRow = applyFixed(exchangeOptions);
  const generatedRow = applyFixed(generatedOptions);

  // useAvailableTokens surfaces isLoading and a 15s timeout error. Two
  // labelled-but-empty rows read as broken, so collapse to a single status
  // line while fetching, on error, or when the registry resolves to nothing.
  if (error) {
    return (
      <div className="token-selector-grouped token-selector-status">Unable to load tokens</div>
    );
  }
  if (exchangeRow.length === 0 && generatedRow.length === 0) {
    return (
      <div className="token-selector-grouped token-selector-status">
        {isLoading ? 'Loading tokens...' : 'No tokens available'}
      </div>
    );
  }

  // InlineOptions renders leftLabel even with zero options, so a fixed token
  // belonging to the other row would leave this row's label dangling. Skip an
  // empty row entirely.
  const renderRow = (label: string, options: OptionType[]) => {
    if (options.length === 0) return null;
    return (
      <InlineOptions
        leftLabel={label}
        options={options}
        selectedOption={token}
        onOptionChange={(option) => handleOptionChange(option as string)}
        classname="widget-token-selector"
        disableOverflow={true}
        optionDisabled={isFixedToken}
      />
    );
  };

  return (
    <div className="token-selector-grouped">
      {renderRow('Exchange', exchangeRow)}
      {renderRow('Generated', generatedRow)}
    </div>
  );
};

const TokenSelector: React.FC<TokenSelectorProps> = ({
  token,
  setToken,
  isFixedToken,
  fromRegistry,
}) => {
  if (fromRegistry) {
    return <RegistryTokenSelector token={token} setToken={setToken} isFixedToken={isFixedToken} />;
  }

  const effectiveTokenlist = isFixedToken ? tokens.filter((t) => t.value === token) : tokens;

  const handleOptionChange = makeHandleOptionChange(isFixedToken, setToken);

  return (
    <InlineOptions
      options={effectiveTokenlist}
      selectedOption={token}
      onOptionChange={(option) => handleOptionChange(option as string)}
      classname="widget-token-selector"
      maxVisibleOptions={10}
      isCalculateVisibleCount={true}
    />
  );
};

export default TokenSelector;
